import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import { UhxShopifyMap } from 'content/products/uhx';
import { getMaxConfig } from './index';
import { getMaxPlanMonths } from './plan';
import type { MaxLibraryEntry, MaxLibraryKey } from './library';

/**
 * The "What you get" table. Each row's price comes from the product's own
 * Shopify map wherever one exists, so the modal never drifts from what the
 * add-on actually costs. The four products with no map are authored below.
 *
 * Every Shopify map prices a year and nothing in the repo prices those add-ons
 * monthly, so their monthly figure stays derived (`/ 12`). The authored rows
 * carry a real monthly list price where ops has one. The two "bought
 * separately" totals are therefore summed independently.
 */

/**
 * Les Mills, Migraine Insights, Driving Insights, Health EX and Jade AI are
 * not sold standalone on the site, so there is no Shopify map to read. These are the
 * list prices from ops (App Store Connect, 24-Sep-2026), per store, per term.
 *
 * `monthly` is optional because that is the state of the data. The later ops
 * sheet added a "ROW, US" column, so Les Mills and Migraine now carry a real
 * USD monthly; Driving Insights (Tesla) is $6.90 in `config/powerplugs.yml` for
 * US and ROW. Jade's yearly is exactly 12x its monthly —
 * it is a derived figure, not a sold SKU, so the fallback is already right.
 */
interface AuthoredPrice {
  /** One year in store currency. */
  yearly: number;
  /** Real monthly list price. Absent falls back to `yearly / 12`. */
  monthly?: number;
}

const AUTHORED: Partial<
  Record<MaxLibraryKey, Partial<Record<ShopifyStore, AuthoredPrice>>>
> = {
  lesmills: {
    [ShopifyStore.US]: { yearly: 99, monthly: 11.99 },
    [ShopifyStore.GB]: { yearly: 99, monthly: 11.99 },
    [ShopifyStore.EU]: { yearly: 99, monthly: 12.99 },
    [ShopifyStore.CA]: { yearly: 129, monthly: 14.99 },
    [ShopifyStore.AU]: { yearly: 149, monthly: 19.99 },
    [ShopifyStore.AE]: { yearly: 399.99, monthly: 49.99 },
    [ShopifyStore.SA]: { yearly: 399.99, monthly: 49.99 },
    [ShopifyStore.IN]: { yearly: 9900, monthly: 1199 },
    [ShopifyStore.ROW]: { yearly: 99, monthly: 11.99 },
    [ShopifyStore.MX]: { yearly: 1999, monthly: 249 },
    [ShopifyStore.ZA]: { yearly: 1999.99, monthly: 249.99 },
    [ShopifyStore.JP]: { yearly: 15000, monthly: 2000 },
  },
  migraine: {
    [ShopifyStore.US]: { yearly: 49.99, monthly: 5.99 },
    [ShopifyStore.GB]: { yearly: 44.99, monthly: 4.99 },
    [ShopifyStore.EU]: { yearly: 49.99, monthly: 5.99 },
    [ShopifyStore.CA]: { yearly: 69.99, monthly: 7.99 },
    [ShopifyStore.AU]: { yearly: 79.99, monthly: 9.99 },
    [ShopifyStore.AE]: { yearly: 199.99, monthly: 21.99 },
    [ShopifyStore.SA]: { yearly: 199.99, monthly: 24.99 },
    [ShopifyStore.IN]: { yearly: 4499, monthly: 499 },
    [ShopifyStore.ROW]: { yearly: 49.99, monthly: 5.99 },
    [ShopifyStore.MX]: { yearly: 999, monthly: 129 },
    [ShopifyStore.ZA]: { yearly: 999.99, monthly: 119.99 },
    [ShopifyStore.JP]: { yearly: 8000, monthly: 1000 },
  },
  tesla: {
    [ShopifyStore.US]: { yearly: 69, monthly: 6.9 },
    [ShopifyStore.GB]: { yearly: 69, monthly: 6.9 },
    [ShopifyStore.EU]: { yearly: 79, monthly: 7.9 },
    [ShopifyStore.CA]: { yearly: 99, monthly: 9.9 },
    [ShopifyStore.AU]: { yearly: 99, monthly: 9.9 },
    [ShopifyStore.AE]: { yearly: 249.99, monthly: 24.99 },
    [ShopifyStore.SA]: { yearly: 299.99, monthly: 29.99 },
    [ShopifyStore.IN]: { yearly: 6900, monthly: 699 },
    [ShopifyStore.ROW]: { yearly: 69, monthly: 6.9 },
    [ShopifyStore.MX]: { yearly: 1499, monthly: 149 },
    [ShopifyStore.ZA]: { yearly: 1499.99, monthly: 149.99 },
    [ShopifyStore.JP]: { yearly: 11000, monthly: 1100 },
  },
  jade: {
    [ShopifyStore.US]: { yearly: 47.88 },
    [ShopifyStore.GB]: { yearly: 47.88, monthly: 3.99 },
    [ShopifyStore.EU]: { yearly: 47.88, monthly: 3.99 },
    [ShopifyStore.CA]: { yearly: 59.88, monthly: 4.99 },
    [ShopifyStore.AU]: { yearly: 71.88, monthly: 5.99 },
    [ShopifyStore.AE]: { yearly: 179.88, monthly: 14.99 },
    [ShopifyStore.SA]: { yearly: 215.88, monthly: 17.99 },
    [ShopifyStore.IN]: { yearly: 4788, monthly: 399 },
    [ShopifyStore.ROW]: { yearly: 47.88 },
    [ShopifyStore.MX]: { yearly: 1068, monthly: 89 },
    [ShopifyStore.ZA]: { yearly: 959.88, monthly: 79.99 },
    [ShopifyStore.JP]: { yearly: 7200, monthly: 600 },
  },
  // US-only (see `features.ts`); list price from the product team, 7-Oct-2026.
  healthex: {
    [ShopifyStore.US]: { yearly: 49.99, monthly: 4.99 },
  },
};

/** A row with no price, or a zero/absent variant, is dropped from the table. */
const usable = (value: number | undefined | null): number | null =>
  typeof value === 'number' && value > 0 ? value : null;

const hasVariant = (variantId: string | undefined): boolean =>
  Boolean(variantId && variantId.trim());

export interface MaxPriceRow {
  key: MaxLibraryKey;
  /** One year in store currency. */
  yearly: number;
  /** Authored where ops supplied one, otherwise `yearly / 12`. */
  monthly: number;
  /** UltrahumanX is quoted per year in the Figma; everything else per month. */
  quote: 'monthly' | 'yearly';
}

/** The five rows backed by a Shopify map, which only ever prices a year. */
const mappedYearly = (
  key: MaxLibraryKey,
  store: ShopifyStore,
  isCnOPlus: boolean,
): number | null => {
  switch (key) {
    case 'afib': {
      const cfg = AfibShopifyMap[store]?.Year1;
      return hasVariant(cfg?.variantId) ? usable(cfg?.price) : null;
    }
    case 'cardio': {
      const cfg = CardioAdaptibilityShopifyMap[store]?.Year1;
      return hasVariant(cfg?.variantId) ? usable(cfg?.price) : null;
    }
    case 'cycle': {
      const map = isCnOPlus ? CnoPlusShopifyMap : CnoProShopifyMap;
      return usable(map[store]?.Year1?.price);
    }
    case 'respiratory':
      return usable(RespiratoryShopifyMap[store]?.Year1?.price);
    case 'uhx':
      return usable(UhxShopifyMap[store]?.Year1?.pricePerYear);
    default:
      return null;
  }
};

const rowPrice = (
  key: MaxLibraryKey,
  store: ShopifyStore,
  isCnOPlus: boolean,
): Pick<MaxPriceRow, 'yearly' | 'monthly'> | null => {
  // Keyed on the product, not the store entry: an authored product with no
  // price for this store is unpriced, not a candidate for the Shopify maps.
  if (AUTHORED[key]) {
    const entry = AUTHORED[key]?.[store];
    const yearly = usable(entry?.yearly);
    if (yearly === null) return null;
    return { yearly, monthly: usable(entry?.monthly) ?? yearly / 12 };
  }
  const yearly = mappedYearly(key, store, isCnOPlus);
  return yearly === null ? null : { yearly, monthly: yearly / 12 };
};

export interface MaxValueSummary {
  rows: MaxPriceRow[];
  /** Add-ons bought separately for one month (per-row quotes stay monthly). */
  separatelyMonthly: number;
  separatelyYearly: number;
  /** Price of the short MAX plan — one cycle of `maxMonthlyMonths` months. */
  maxMonthly: number | null;
  /** Cycle length of the short plan: 1 everywhere but IN (3). */
  maxMonthlyMonths: number;
  /** Add-ons bought separately for one short-plan cycle (`separatelyMonthly × months`). */
  separatelyShortCycle: number;
  maxYearly: number | null;
  /** The larger of the monthly and yearly savings, hence "up to N%". */
  savePercent: number | null;
}

export const getMaxValueSummary = (
  entries: MaxLibraryEntry[],
  store: ShopifyStore | string | null | undefined,
  isCnOPlus: boolean,
): MaxValueSummary => {
  const resolvedStore = store as ShopifyStore;
  const rows: MaxPriceRow[] = [];

  for (const entry of entries) {
    const price = resolvedStore
      ? rowPrice(entry.key, resolvedStore, isCnOPlus)
      : null;
    if (price === null) continue;
    rows.push({
      key: entry.key,
      yearly: price.yearly,
      monthly: price.monthly,
      quote: entry.key === 'uhx' ? 'yearly' : 'monthly',
    });
  }

  // Summed independently: monthly is no longer yearly/12 on every row.
  const separatelyMonthly = rows.reduce((sum, row) => sum + row.monthly, 0);
  const separatelyYearly = rows.reduce((sum, row) => sum + row.yearly, 0);

  const maxConfig = getMaxConfig(store);
  const maxMonthly = maxConfig?.plans.monthly.price ?? null;
  const maxMonthlyMonths = maxConfig
    ? getMaxPlanMonths(maxConfig, 'monthly')
    : 1;
  const separatelyShortCycle = separatelyMonthly * maxMonthlyMonths;
  const maxYearly = maxConfig?.plans.yearly.price ?? null;

  let savePercent: number | null = null;
  if (separatelyMonthly > 0 && (maxMonthly !== null || maxYearly !== null)) {
    const candidates = [
      maxMonthly !== null ? 1 - maxMonthly / separatelyShortCycle : 0,
      maxYearly !== null && separatelyYearly > 0
        ? 1 - maxYearly / separatelyYearly
        : 0,
    ];
    const best = Math.max(...candidates);
    savePercent = best > 0 ? Math.round(best * 100) : null;
  }

  return {
    rows,
    separatelyMonthly,
    separatelyYearly,
    maxMonthly,
    maxMonthlyMonths,
    separatelyShortCycle,
    maxYearly,
    savePercent,
  };
};
